import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkApiHealth } from "services/api";
import { Routes } from "../../navigation";
import { OfflinePageCmp } from "./types";
import { Button } from "ui/components/Button/Button";

/**
 * Offline page that displays when API is unreachable
 * You can navigate users here when network errors are detected
 */
const OfflinePage: OfflinePageCmp = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);

  const handleRetry = async () => {
    setIsChecking(true);
    const isHealthy = await checkApiHealth();
    setIsChecking(false);

    if (isHealthy) {
      // API is back online, navigate back or to home
      //navigate(-1); // Go back to previous page
      navigate('/'); // Go to home
    } else {
      // Still offline, could show a message
      alert("Still unable to connect. Please try again later.");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="max-w-md w-full bg-white shadow-xl rounded-lg p-8">
        <div className="flex flex-col items-center text-center">
          {/* Offline Icon */}
          <div className="mb-6">
            <svg
              className="w-20 h-20 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            You're Offline
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6">
            We can't connect to the server right now. Please check your
            internet connection and try again.
          </p>

          {/* Troubleshooting Tips */}
          <div className="w-full bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Troubleshooting tips:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Check your internet connection</li>
              <li>Try refreshing the page</li>
              <li>Check if other websites are working</li>
              <li>Wait a moment and try again</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 w-full">
            <Button
              onClick={handleRetry}
              disabled={isChecking}
              variant="primary"
              size="full"
            //className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isChecking ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Checking...
                </span>
              ) : (
                "Try Again"
              )}
            </Button>

            <Button
              onClick={() => window.location.reload()}
              variant="outlined"
              size="full"
            //className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export with route for easy routing
OfflinePage.route = Routes.OfflinePage;

export default OfflinePage;